import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Award,
  Users,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  Clock,
  Calendar,
  ChevronLeft
} from 'lucide-react';
import { useAuth } from '../contexts/JWTAuthContext';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './NpsLeaderboard.css';

const NpsLeaderboard = () => {
  const [activeTab, setActiveTab] = useState('leaderboard'); // 'leaderboard' | 'history'

  // Leaderboard state
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('nps_score');
  const [sortDir, setSortDir] = useState('desc');
  const [overallNps, setOverallNps] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateDetail, setDateDetail] = useState(null);
  const [dateDetailLoading, setDateDetailLoading] = useState(false);

  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const fetchScores = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/nps/scores');
      if (response.data.success) {
        setScores(response.data.scores || []);
        setOverallNps(response.data.overallNps || 0);
        setLastSyncedAt(response.data.lastSyncedAt);
      }
    } catch (error) {
      console.error('Error fetching NPS scores:', error);
      showError('Failed to fetch NPS scores');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await api.get('/api/nps/history');
      if (response.data.success) {
        setHistory(response.data.history || []);
      }
    } catch (error) {
      console.error('Error fetching NPS history:', error);
      showError('Failed to fetch NPS history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchDateDetail = async (date) => {
    try {
      setDateDetailLoading(true);
      setSelectedDate(date);
      const response = await api.get('/api/nps/history', { params: { date } });
      if (response.data.success) {
        setDateDetail(response.data);
      }
    } catch (error) {
      console.error('Error fetching date detail:', error);
      showError('Failed to fetch per-employee breakdown');
    } finally {
      setDateDetailLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await api.post('/api/nps/sync');
      if (response.data.success) {
        showSuccess(`Sync complete: ${response.data.employeesProcessed} employees, ${response.data.responsesProcessed} responses`);
        await fetchScores();
        if (activeTab === 'history') await fetchHistory();
      }
    } catch (error) {
      console.error('NPS sync error:', error);
      showError(error.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchScores();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'history' && history.length === 0) {
      fetchHistory();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="sort-indicator" />;
    return sortDir === 'asc'
      ? <ArrowUp size={14} className="sort-indicator active" />
      : <ArrowDown size={14} className="sort-indicator active" />;
  };

  const filteredAndSorted = useMemo(() => {
    let filtered = scores;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = scores.filter(s =>
        (s.sales_taker_code || '').toLowerCase().includes(term) ||
        (s.sales_taker_name || '').toLowerCase().includes(term) ||
        (s.branch || '').toLowerCase().includes(term)
      );
    }

    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (typeof aVal === 'number' || ['nps_score', 'total_responses', 'promoters', 'neutrals', 'detractors'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [scores, searchTerm, sortField, sortDir]);

  const getNpsClass = (score) => {
    if (score >= 50) return 'positive';
    if (score >= 0) return 'neutral';
    return 'negative';
  };

  const getRankClass = (idx) => {
    if (idx === 0) return 'top-1';
    if (idx === 1) return 'top-2';
    if (idx === 2) return 'top-3';
    return 'default';
  };

  const stats = useMemo(() => {
    if (scores.length === 0) return { highest: 0, lowest: 0, totalEmployees: 0 };
    const npsValues = scores.map(s => Number(s.nps_score));
    return {
      highest: Math.max(...npsValues),
      lowest: Math.min(...npsValues),
      totalEmployees: scores.length
    };
  }, [scores]);

  if (loading) {
    return (
      <div className="nps-loading">
        <div className="nps-loading-spinner" />
        <p>Loading NPS scores...</p>
      </div>
    );
  }

  return (
    <div className="nps-leaderboard">
      {/* Header */}
      <div className="nps-header">
        <div className="nps-header-content">
          <h1 className="nps-title">NPS Leaderboard</h1>
          <p className="nps-subtitle">
            {lastSyncedAt && (
              <span><Clock size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                Last synced: {new Date(lastSyncedAt).toLocaleString()}
              </span>
            )}
          </p>
        </div>
        {user && user.role === 'admin' && (
          <motion.button
            className="nps-sync-btn"
            onClick={handleSync}
            disabled={syncing}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw size={16} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </motion.button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="nps-stats">
        <motion.div className="nps-stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <div className="nps-stat-label"><TrendingUp size={16} /> Overall NPS</div>
          <div className={`nps-stat-value ${getNpsClass(overallNps)}`}>
            {overallNps.toFixed(1)}%
          </div>
        </motion.div>
        <motion.div className="nps-stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="nps-stat-label"><Users size={16} /> Total Employees</div>
          <div className="nps-stat-value">{stats.totalEmployees}</div>
        </motion.div>
        <motion.div className="nps-stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <div className="nps-stat-label"><TrendingUp size={16} /> Highest NPS</div>
          <div className={`nps-stat-value ${getNpsClass(stats.highest)}`}>
            {stats.highest.toFixed(1)}%
          </div>
        </motion.div>
        <motion.div className="nps-stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <div className="nps-stat-label"><TrendingDown size={16} /> Lowest NPS</div>
          <div className={`nps-stat-value ${getNpsClass(stats.lowest)}`}>
            {stats.lowest.toFixed(1)}%
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="nps-tabs">
        <button
          className={`nps-tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          <Award size={16} /> Leaderboard
        </button>
        <button
          className={`nps-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => { setActiveTab('history'); setSelectedDate(null); setDateDetail(null); }}
        >
          <Calendar size={16} /> History
        </button>
      </div>

      {activeTab === 'leaderboard' && (
        <>
          {/* Search */}
          <div className="nps-search">
            <div className="nps-search-wrapper">
              <Search size={16} className="nps-search-icon" />
              <input
                type="text"
                className="nps-search-input"
                placeholder="Search by code, name, or branch..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          {filteredAndSorted.length === 0 ? (
            <div className="nps-empty">
              <Award size={48} />
              <p>No NPS scores found. {scores.length === 0 ? 'Run a sync to fetch data.' : 'Try a different search.'}</p>
            </div>
          ) : (
            <div className="nps-table-container">
              <table className="nps-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th onClick={() => handleSort('sales_taker_code')}>Employee Code <SortIcon field="sales_taker_code" /></th>
                    <th onClick={() => handleSort('sales_taker_name')}>Name <SortIcon field="sales_taker_name" /></th>
                    <th onClick={() => handleSort('branch')}>Branch <SortIcon field="branch" /></th>
                    <th onClick={() => handleSort('nps_score')}>NPS Score <SortIcon field="nps_score" /></th>
                    <th onClick={() => handleSort('total_responses')}>Responses <SortIcon field="total_responses" /></th>
                    <th onClick={() => handleSort('promoters')}>Promoters <SortIcon field="promoters" /></th>
                    <th onClick={() => handleSort('neutrals')}>Neutrals <SortIcon field="neutrals" /></th>
                    <th onClick={() => handleSort('detractors')}>Detractors <SortIcon field="detractors" /></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.map((score, idx) => (
                    <motion.tr
                      key={score.sales_taker_code}
                      className="nps-row"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.02 }}
                    >
                      <td><span className={`nps-rank ${getRankClass(idx)}`}>{idx + 1}</span></td>
                      <td className="nps-cell-primary">{score.sales_taker_code}</td>
                      <td className={score.sales_taker_name ? 'nps-cell-primary' : 'nps-cell-secondary'}>
                        {score.sales_taker_name || '—'}
                      </td>
                      <td className={score.branch ? 'nps-cell-primary' : 'nps-cell-secondary'}>
                        {score.branch || '—'}
                      </td>
                      <td>
                        <span className={`nps-score-badge ${getNpsClass(Number(score.nps_score))}`}>
                          {Number(score.nps_score).toFixed(1)}%
                        </span>
                      </td>
                      <td className="nps-cell-count">{score.total_responses}</td>
                      <td className="nps-cell-count promoters">{score.promoters}</td>
                      <td className="nps-cell-count neutrals">{score.neutrals}</td>
                      <td className="nps-cell-count detractors">{score.detractors}</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'history' && (
        <>
          {selectedDate ? (
            <div className="nps-history-detail">
              <button className="nps-back-btn" onClick={() => { setSelectedDate(null); setDateDetail(null); }}>
                <ChevronLeft size={16} /> Back to all dates
              </button>

              {dateDetailLoading ? (
                <div className="nps-loading"><div className="nps-loading-spinner" /><p>Loading...</p></div>
              ) : dateDetail && (
                <>
                  <div className="nps-detail-header">
                    <h2>{new Date(selectedDate + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h2>
                    <div className="nps-detail-summary">
                      <span className={`nps-score-badge ${getNpsClass(dateDetail.overallNps)}`}>
                        Overall {dateDetail.overallNps.toFixed(1)}%
                      </span>
                      <span className="nps-detail-count">{dateDetail.totalResponses} responses</span>
                    </div>
                  </div>

                  {dateDetail.employees.length === 0 ? (
                    <div className="nps-empty"><Award size={48} /><p>No responses on this date.</p></div>
                  ) : (
                    <div className="nps-table-container">
                      <table className="nps-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Employee Code</th>
                            <th>Name</th>
                            <th>Branch</th>
                            <th>NPS Score</th>
                            <th>Responses</th>
                            <th>Promoters</th>
                            <th>Neutrals</th>
                            <th>Detractors</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dateDetail.employees.map((e, idx) => (
                            <tr key={e.sales_taker_code} className="nps-row">
                              <td><span className={`nps-rank ${getRankClass(idx)}`}>{idx + 1}</span></td>
                              <td className="nps-cell-primary">{e.sales_taker_code}</td>
                              <td className={e.sales_taker_name ? 'nps-cell-primary' : 'nps-cell-secondary'}>
                                {e.sales_taker_name || '—'}
                              </td>
                              <td className={e.branch ? 'nps-cell-primary' : 'nps-cell-secondary'}>
                                {e.branch || '—'}
                              </td>
                              <td>
                                <span className={`nps-score-badge ${getNpsClass(Number(e.nps_score))}`}>
                                  {Number(e.nps_score).toFixed(1)}%
                                </span>
                              </td>
                              <td className="nps-cell-count">{e.total_responses}</td>
                              <td className="nps-cell-count promoters">{e.promoters}</td>
                              <td className="nps-cell-count neutrals">{e.neutrals}</td>
                              <td className="nps-cell-count detractors">{e.detractors}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : historyLoading ? (
            <div className="nps-loading"><div className="nps-loading-spinner" /><p>Loading history...</p></div>
          ) : history.length === 0 ? (
            <div className="nps-empty">
              <Calendar size={48} />
              <p>No dated responses yet. Run a sync to populate history.</p>
            </div>
          ) : (
            <div className="nps-table-container">
              <table className="nps-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>NPS Score</th>
                    <th>Responses</th>
                    <th>Promoters</th>
                    <th>Neutrals</th>
                    <th>Detractors</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((d, idx) => (
                    <motion.tr
                      key={d.response_date}
                      className="nps-row nps-row-clickable"
                      onClick={() => fetchDateDetail(d.response_date)}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.02 }}
                    >
                      <td className="nps-cell-primary">
                        {new Date(d.response_date + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td>
                        <span className={`nps-score-badge ${getNpsClass(Number(d.nps_score))}`}>
                          {Number(d.nps_score).toFixed(1)}%
                        </span>
                      </td>
                      <td className="nps-cell-count">{d.total_responses}</td>
                      <td className="nps-cell-count promoters">{d.promoters}</td>
                      <td className="nps-cell-count neutrals">{d.neutrals}</td>
                      <td className="nps-cell-count detractors">{d.detractors}</td>
                      <td className="nps-cell-secondary">Click to drill in →</td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NpsLeaderboard;
